/**
 * Create / Edit revision line, with its members (lineage admin polish).
 *
 * Label + description as before, plus a calibre picker scoped to the
 * manufacturer and one row per member with its revision label, active years
 * and current flag. Save writes the line, then each changed member through
 * setCalibreRevision; removing a member takes it out of the line.
 *
 * Curator values are preserved (revisionLineMembers.ts): rows start from the
 * stored values, and a calibre changed elsewhere while the dialog was open is
 * skipped and reported, not overwritten. The calibre's own Revisions card still
 * edits the same fields.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  getCalibresForManufacturer,
  saveLineage,
  setCalibreRevision,
  type CalibreLineageEntry,
} from '../../services/calibreLineage';
import { sortMembers } from '../../services/calibreLineageLogic';
import { mixedCalibreNumbers, mixedNumbersText, splitRevisionSuffix } from '../../services/revisionClusters';
import {
  draftErrors,
  draftFromStored,
  planMemberWrites,
  type MemberDraft,
} from '../../services/revisionLineMembers';
import type { CustomCalibreEntry } from '../../services/firebase';
import { DEFAULT_REVISION_TERMS, type RevisionTerms } from '../../services/revisionTerms';

interface Props {
  manufacturerId: string;
  /** Null = create a new line. */
  lineage: CalibreLineageEntry | null;
  /** The line's current members (edit mode). */
  members: CustomCalibreEntry[];
  /** Every line of the manufacturer, to name the line a calibre already belongs to. */
  lineages: CalibreLineageEntry[];
  /** The maker's revision words; default Revision / Revisions. */
  terms?: RevisionTerms;
  onClose: () => void;
  onSaved: () => void;
}

const PICKER_LIMIT = 20;

export function RevisionLineEditor({
  manufacturerId,
  lineage,
  members,
  lineages,
  terms = DEFAULT_REVISION_TERMS,
  onClose,
  onSaved,
}: Props) {
  const [baseLabel, setBaseLabel] = useState(lineage?.baseLabel ?? '');
  const [description, setDescription] = useState(lineage?.description ?? '');
  const [drafts, setDrafts] = useState<MemberDraft[]>(() => sortMembers(members).map(draftFromStored));
  const [removed, setRemoved] = useState<string[]>([]);
  const [calibres, setCalibres] = useState<CustomCalibreEntry[] | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCalibresForManufacturer(manufacturerId)
      .then((cs) => !cancelled && setCalibres(cs))
      .catch((err) => {
        console.error('Failed to load calibres for the revision line editor', err);
        toast.error('Could not load calibres');
        if (!cancelled) setCalibres([]);
      });
    return () => {
      cancelled = true;
    };
  }, [manufacturerId]);

  // What the curator saw: the members passed in, plus the picker's load.
  const opened = useMemo(() => {
    const m = new Map<string, CustomCalibreEntry>();
    for (const c of calibres ?? []) m.set(c.id, c);
    for (const c of members) m.set(c.id, c);
    return m;
  }, [calibres, members]);
  const lineName = useMemo(() => new Map(lineages.map((l) => [l.id, l.baseLabel])), [lineages]);

  const inDraft = new Set(drafts.map((d) => d.id));
  const q = search.trim().toLowerCase();
  const candidates = (calibres ?? [])
    .filter((c) => !c.deprecated && !inDraft.has(c.id))
    .filter(
      (c) => !q || c.name.toLowerCase().includes(q) || (c.alsoKnownAs ?? []).some((a) => a.toLowerCase().includes(q)),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const otherLine = (c: CustomCalibreEntry) =>
    c.calibreLineageId && c.calibreLineageId !== lineage?.id ? c.calibreLineageId : null;

  const add = (c: CustomCalibreEntry) => {
    setDrafts([...drafts, draftFromStored(c)]);
    setRemoved(removed.filter((id) => id !== c.id));
    setSearch('');
  };
  const remove = (id: string) => {
    setDrafts(drafts.filter((d) => d.id !== id));
    if (members.some((m) => m.id === id)) setRemoved([...removed, id]);
  };
  const patch = (id: string, p: Partial<MemberDraft>) =>
    setDrafts(
      drafts.map((d) => {
        if (d.id === id) return { ...d, ...p };
        // One current per line: marking one un-marks the others in the form.
        return p.isCurrent ? { ...d, isCurrent: false } : d;
      }),
    );

  const errors = new Map(drafts.map((d) => [d.id, draftErrors(d)]));
  const blocked = !baseLabel.trim() || [...errors.values()].some((e) => e.length > 0) || calibres === null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const lineageId = await saveLineage({ baseLabel, manufacturerId, description }, lineage?.id);
      const fresh = new Map((await getCalibresForManufacturer(manufacturerId)).map((c) => [c.id, c]));
      const plan = planMemberWrites({ lineageId, opened, fresh, drafts, removedIds: removed });
      for (const w of plan.writes) await setCalibreRevision(w.id, w.fields);
      toast.success(
        `${lineage ? 'Revision line updated' : 'Revision line created'}${
          plan.writes.length ? ` · ${plan.writes.length} revision${plan.writes.length === 1 ? '' : 's'} saved` : ''
        }`,
      );
      for (const s of plan.skipped) toast.error(`${s.name} not saved: it ${s.reason}.`, { duration: 8000 });
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'rounded-md border border-gray-300 px-2 py-1 text-sm';

  return (
    <Modal isOpen onClose={onClose} title={lineage ? 'Edit revision line' : 'New revision line'} size="xl" dismissOnBackdrop={false}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="font-medium text-gray-700">Label</span>
          <input
            value={baseLabel}
            onChange={(e) => setBaseLabel(e.target.value)}
            placeholder="ETA Calibre 2824"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          {!lineage && <span className="text-xs text-gray-400">The id is made from the label and cannot change later.</span>}
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700">Description</span>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">{terms.plural}</p>
          {drafts.length === 0 ? (
            <p className="text-xs text-gray-400 italic">
              No {terms.plural.toLowerCase()} yet. Pick this manufacturer&apos;s calibres below — or save now and set them later.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1 pr-2">Calibre</th>
                  <th className="py-1 pr-2">{terms.singular} label</th>
                  <th className="py-1 pr-2">Active from</th>
                  <th className="py-1 pr-2">Active until</th>
                  <th className="py-1 pr-2">Current</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => {
                  const rowErrors = errors.get(d.id) ?? [];
                  return (
                    <tr key={d.id} className="align-top">
                      <td className="py-1 pr-2 text-gray-900">
                        {d.name}
                        {d.isPlaceholder && (
                          <span className="block text-xs text-gray-500">Placeholder — no label, years or current.</span>
                        )}
                        {rowErrors.map((e) => (
                          <span key={e} className="block text-xs text-red-600">
                            {e}
                          </span>
                        ))}
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          aria-label={`Revision label for ${d.name}`}
                          value={d.revisionLabel}
                          disabled={d.isPlaceholder}
                          placeholder={splitRevisionSuffix(d.name)?.suffix ?? ''}
                          onChange={(e) => patch(d.id, { revisionLabel: e.target.value })}
                          className={`${inputClass} w-24 font-mono`}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          aria-label={`Active from for ${d.name}`}
                          value={d.from}
                          disabled={d.isPlaceholder}
                          inputMode="numeric"
                          placeholder="YYYY"
                          onChange={(e) => patch(d.id, { from: e.target.value })}
                          className={`${inputClass} w-20 tabular-nums`}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          aria-label={`Active until for ${d.name}`}
                          value={d.until}
                          disabled={d.isPlaceholder}
                          inputMode="numeric"
                          placeholder="YYYY"
                          onChange={(e) => patch(d.id, { until: e.target.value })}
                          className={`${inputClass} w-20 tabular-nums`}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          type="checkbox"
                          aria-label={`${d.name} is current`}
                          checked={d.isCurrent}
                          disabled={d.isPlaceholder}
                          onChange={(e) => patch(d.id, { isCurrent: e.target.checked })}
                        />
                      </td>
                      <td className="py-1">
                        <Button size="sm" variant="ghost" onClick={() => remove(d.id)}>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {mixedCalibreNumbers(drafts.map((d) => d.name)).length > 0 && (
            <p className="text-xs text-amber-700">{mixedNumbersText(mixedCalibreNumbers(drafts.map((d) => d.name)))}</p>
          )}
          {removed.length > 0 && (
            <p className="text-xs text-amber-700">
              {removed.length} {(removed.length === 1 ? terms.singular : terms.plural).toLowerCase()} will leave this line on Save and lose
              {removed.length === 1 ? ' its' : ' their'} revision label, years and current flag.
            </p>
          )}

          <div className="rounded-md border border-gray-200 p-2 space-y-1">
            <input
              aria-label="Add a calibre"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={calibres === null ? 'Loading calibres…' : 'Add a calibre — search by name or AKA'}
              disabled={calibres === null}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
            {q && (
              <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                {candidates.length === 0 && <li className="py-1 text-xs text-gray-500 italic">No matching calibres.</li>}
                {candidates.slice(0, PICKER_LIMIT).map((c) => {
                  const other = otherLine(c);
                  return (
                    <li key={c.id} className="flex items-center justify-between gap-2 py-1">
                      <span className="text-sm text-gray-900">
                        {c.name}
                        {other && (
                          <span className="ml-2 text-xs text-gray-500">in {lineName.get(other) ?? other}</span>
                        )}
                      </span>
                      <span title={other ? 'Already in another revision line — remove it there first.' : undefined}>
                        <Button size="sm" variant="secondary" disabled={!!other} onClick={() => add(c)}>
                          Add
                        </Button>
                      </span>
                    </li>
                  );
                })}
                {candidates.length > PICKER_LIMIT && (
                  <li className="py-1 text-xs text-gray-500">{candidates.length - PICKER_LIMIT} more — refine the search.</li>
                )}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={blocked}>
            {lineage ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
